import API_BASE_URL from 'src/constants';
import { useState, useEffect } from 'react';
import { validateEmail } from 'src/utils/general';
import { toast } from 'react-toastify';
import i18n from 'src/localization/i18n';
import logger from 'src/utils/logger';
import { $apiFetchClient } from './api';
import { fetchAuthenticatedProfile } from './authProfile';
import { getToken } from './authToken';
import { useAuthToken } from '../hooks/useAuthHook';

export interface Profile {
  id: number | null;
  email: string | null;
  lang: string | null;
  name: string | null;
  phone: string | null;
  password: string | null;
  tagline?: string | null;
  birthday?: string | null;
  notification_daily_updates: boolean | null;
  notification_email: boolean | null;
  notification_sms: boolean | null;
  is_in_dark_mode: boolean | null;
  newPassword?: string | null;
  newPasswordConfirmation?: string | null;
}

export const profileDataPlaceholder: Profile = {
  id: null,
  name: null,
  email: null,
  phone: null,
  lang: 'de_DE',
  password: null,
  newPassword: null,
  newPasswordConfirmation: null,
  tagline: null,
  birthday: null,
  notification_daily_updates: null,
  notification_email: null,
  notification_sms: null,
  is_in_dark_mode: false,
};

export type PasswordUpdatePayload = {
  password: string;
  newPassword: string;
};

interface UserProfileInterface {
  profile: Profile;
  picture: string | null;
  loading: boolean;
  error: string | null;
  updateUserPasswords: (password: string, newPassword: string) => Promise<boolean>;
  updateProfileData: (profileData: Profile, silent?: boolean) => Promise<void>;
  updateProfilePicture: (file: File | null) => void;
  deleteProfilePicture: () => Promise<void>;
  updateDarkMode: (value: boolean) => Promise<void>;
  fetchUserProfile: () => void;
}

/**
 * Custom hook to manage user profile data.
 * @returns {Object} - The profile data, loading state, error state, and updateProfileData function.
 */
const useUserProfile = (): UserProfileInterface => {
  const getAccessToken = getToken;
  const [profile, setProfile] = useState(profileDataPlaceholder);
  const [picture, setPicture] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetches the user profile data from the API.
   */
  const fetchUserProfile = async () => {
    if (!getAccessToken()) return;

    try {
      // The 401-retry that used to be inlined here now lives in
      // `authProfile.ts`, shared with <Auth> — which lacked it and therefore
      // discarded valid, freshly issued tokens.
      const response = await fetchAuthenticatedProfile();

      if (!response || !response.ok) throw new Error();

      setProfile(await response.json());
    } catch (error) {
      logger.error('Failed to fetch profile data', error);
      setError('Failed to fetch profile data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates the user password. The backend invalidates every token of the user
   * afterwards, so the caller has to end the session; no further request is
   * made here and the caller reports the outcome.
   * @param {string} password - The current password.
   * @param {string} newPassword - The new password.
   */
  const updateUserPasswords = async (password: string, newPassword: string): Promise<boolean> => {
    logger.info('Updating user password');
    try {
      const response = await fetch(`${API_BASE_URL}portal/users/password/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ new_password: newPassword, old_password: password }),
      });

      if (!response.ok) throw new Error('Failed to save password');

      return true;
    } catch (error) {
      logger.error('Failed to update password', error);
      toast.error(i18n.t('errors.password_update_failed'));
      return false;
    }
  };

  /**
   * Updates the user profile data.
   * @param {Profile} profileData - The profile data to update.
   * @param {boolean} silent - Suppresses the success toast.
   */
  const updateProfileData = async (profileData: Profile, silent = false) => {
    const { email, password, newPassword, newPasswordConfirmation, ...rest } = profileData;

    if (!email || !validateEmail(email)) return;

    logger.info('Updating profile data');
    try {
      const profilePayload = {
        ...rest,
        email,
      };

      const response = await fetch(`${API_BASE_URL}portal/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify(profilePayload),
      });

      if (!response.ok) throw new Error('Failed to save profile data');

      if (!silent) toast.success(i18n.t('success.profile_data_updated'));
      await fetchUserProfile();
    } catch (error) {
      logger.error('Failed to update profile data', error);
      toast.error(i18n.t('errors.profile_update_failed'));
    }
  };

  /**
   * Persists the dark mode preference on its own, so the theme toggle is saved
   * immediately when it changes (no full profile save / no success toast).
   * Optimistically updates local state to keep ThemeSync consistent.
   * @param {boolean} value - Whether dark mode should be enabled.
   */
  const updateDarkMode = async (value: boolean) => {
    setProfile(prev => ({ ...prev, is_in_dark_mode: value }));
    try {
      const response = await fetch(`${API_BASE_URL}portal/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ is_in_dark_mode: value }),
      });

      if (!response.ok) throw new Error('Failed to save dark mode preference');
    } catch (error) {
      logger.error('Failed to update dark mode preference', error);
    }
  };

  /**
   * Fetches the user profile picture from the API.
   */
  const setUserProfilePictureUrl = async () => {
    if (!getAccessToken()) {
      setError('Failed to fetch profile picture - no auth token');
      return;
    }
    try {
      const resp = await $apiFetchClient.GET('/users/profile/picture', {
        parseAs: 'blob',
      });
      if (resp.response?.status === 200 && resp.data instanceof Blob && resp.data.size > 0) {
        setPicture(URL.createObjectURL(resp.data));
      } else {
        setPicture(null);
      }
    } catch (error) {
      // A transient failure here (e.g. backend restarting or a blocked
      // request) must not bubble up as an uncaught error and crash the page.
      logger.error('Failed to fetch profile picture', error);
      setError('Failed to fetch profile picture');
    }
  };

  /**
   * Updates the user profile picture.
   * @param {File | null} file - The new profile picture file.
   */
  const updateProfilePicture = async (file: File | null) => {
    if (!file) return;

    logger.info('Updating profile picture');
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_BASE_URL}portal/users/profile/picture`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken()}` },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to update profile picture');

      toast.success(i18n.t('success.profile_logo_updated'));
      setUserProfilePictureUrl();
    } catch (error) {
      logger.error('Failed to update profile picture', error);
      setError('Failed to update profile picture');
      toast.error(i18n.t('errors.profile_logo_update_failed'));
    }
  };

  /**
   * Deletes the user profile picture.
   */
  const deleteProfilePicture = async () => {
    logger.info('Deleting profile picture');
    try {
      const response = await fetch(`${API_BASE_URL}portal/users/profile/picture`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });

      if (!response.ok) throw new Error('Failed to delete profile picture');

      setPicture(null);
      toast.success(i18n.t('success.profile_logo_deleted'));
    } catch (error) {
      logger.error('Failed to delete profile picture', error);
      setError('Failed to delete profile picture');
      toast.error(i18n.t('errors.profile_logo_delete_failed'));
    }
  };

  const token = useAuthToken();

  useEffect(() => {
    if (!token) {
      // Session ended: drop the previous user's identity so a following login
      // can never render stale name/e-mail. `is_in_dark_mode` is set to null
      // (not the placeholder's `false`) so ThemeSync leaves the theme alone —
      // it only reacts to a boolean.
      setProfile({ ...profileDataPlaceholder, is_in_dark_mode: null });
      // Revoke the object URL, do not just drop the reference: a blob URL stays
      // resolvable for the lifetime of the document, so the previous user's
      // profile picture would remain fetchable by anyone who kept the string.
      setPicture(previous => {
        if (previous && previous.startsWith('blob:')) URL.revokeObjectURL(previous);
        return null;
      });
      setError(null);
      return;
    }

    setLoading(true);
    setUserProfilePictureUrl();
    fetchUserProfile();
  }, [token]);

  return {
    profile,
    picture,
    loading,
    error,
    updateUserPasswords,
    updateProfileData,
    updateProfilePicture,
    deleteProfilePicture,
    updateDarkMode,
    fetchUserProfile,
  };
};

export default useUserProfile;
